import React, { useLayoutEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import InviteRegistrationPage from './pages/InviteRegistrationPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import DownloadsPage from './pages/DownloadsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DashboardPage from './pages/DashboardPage';
import HistoryDetailPage from './pages/HistoryDetailPage';
import HistoriesListPage from './pages/HistoriesListPage';
import ProfilePage from './pages/ProfilePage';
import CommunityLibraryPage from './pages/CommunityLibraryPage';
import CommunityCasePage from './pages/CommunityCasePage';
import AdminPage from './pages/AdminPage';
import ProtectedRoute from './components/common/ProtectedRoute';
import RoleProtectedRoute from './components/common/RoleProtectedRoute';
import CaseList from './components/CaseManager/CaseList';
import CaseEditor from './components/CaseManager/CaseEditor';
import CaseViewer from './components/CaseManager/CaseViewer';
import PlatformSettings from './components/Admin/PlatformSettings';
import BillingSettingsPage from './pages/BillingSettingsPage';
import CohortOverviewPage from './pages/CohortOverviewPage';
import UniversityAdminConsolePage from './pages/University/UniversityAdminConsolePage';
import useBillingVisibility from './hooks/useBillingVisibility';
import { scrollPageToTop } from './utils/scroll';
import './App.css';

const edgeToEdgeRoutes = new Set([
  '/',
  '/about',
  '/downloads',
  '/login',
  '/signup',
  '/register/invite',
  '/forgot-password',
]);
const chromeLessRoutes = new Set();
const edgeToEdgeRoutePrefixes = ['/reset-password/', '/verify-email/'];

function ScrollToTop() {
  const location = useLocation();

  useLayoutEffect(() => {
    if (location.hash) {
      return;
    }

    scrollPageToTop();
  }, [location.pathname, location.hash]);

  return null;
}

function AppLayout() {
  const { shouldShowBilling } = useBillingVisibility();
  const location = useLocation();
  const isEdgeToEdgeRoute = edgeToEdgeRoutes.has(location.pathname)
    || edgeToEdgeRoutePrefixes.some((prefix) => location.pathname.startsWith(prefix));
  const isChromeLessRoute = chromeLessRoutes.has(location.pathname);

  return (
    <div className="app">
      <ScrollToTop />
      {!isChromeLessRoute ? <Navbar /> : null}
      <main className={`main-content${isEdgeToEdgeRoute ? ' main-content--edge' : ''}`}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/register/invite" element={<InviteRegistrationPage />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/downloads" element={<DownloadsPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />

          <Route
            path="/dashboard"
            element={(
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/histories"
            element={(
              <ProtectedRoute>
                <HistoriesListPage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/history/:id"
            element={(
              <ProtectedRoute>
                <HistoryDetailPage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/profile"
            element={(
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/community-library"
            element={(
              <ProtectedRoute>
                <CommunityLibraryPage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/community-library/:id"
            element={(
              <ProtectedRoute>
                <CommunityCasePage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/account/billing"
            element={!shouldShowBilling ? (
              <Navigate replace to="/dashboard" />
            ) : (
              <ProtectedRoute allowedAccountTypes={['individual']}>
                <BillingSettingsPage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/cohorts"
            element={(
              <ProtectedRoute allowedAccountTypes={['university']}>
                <CohortOverviewPage />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/university/admin"
            element={(
              <ProtectedRoute allowedAccountTypes={['university']}>
                <RoleProtectedRoute allowedRoles={['admin', 'instructor']}>
                  <UniversityAdminConsolePage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            )}
          />
          <Route
            path="/platform-settings"
            element={(
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={['global_admin']}>
                  <PlatformSettings />
                </RoleProtectedRoute>
              </ProtectedRoute>
            )}
          />
          <Route
            path="/cases"
            element={(
              <ProtectedRoute>
                <CaseList />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/cases/new"
            element={(
              <ProtectedRoute>
                <CaseEditor />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/cases/:id/edit"
            element={(
              <ProtectedRoute>
                <CaseEditor />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/cases/:id"
            element={(
              <ProtectedRoute>
                <CaseViewer />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/admin"
            element={(
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <AdminPage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            )}
          />
        </Routes>
      </main>
      {!isChromeLessRoute ? <Footer /> : null}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppLayout />
    </Router>
  );
}

export default App;
